// Completion animations - fired when an end user finishes a tour or ticks
// the last item in a checklist. Off by default; opt-in per flow via the
// snapshot's `config.onCompleteAnimation`.
//
// Confetti uses canvas-confetti (~2.5KB gz). Two staggered bursts from the
// bottom corners give a wider spread than a single center burst, and the
// workspace's brand color seeds the palette so completions feel on-brand
// rather than generic carnival-tone.

import confetti from 'canvas-confetti';
import type { FlowConfig, OnCompleteAnimation } from './types.js';

export function fireCompletionAnimation(
  config: FlowConfig | undefined,
  primaryColor: string | undefined,
): void {
  const kind: OnCompleteAnimation =
    (config?.onCompleteAnimation as OnCompleteAnimation | undefined) ?? 'none';
  if (kind === 'none') return;
  if (kind === 'confetti') {
    fireConfetti(primaryColor);
    return;
  }
}

// Multi-page tours that complete via an anchor-click (form submit, button
// that navigates) tear the whole page down a few ms after the animation
// starts - confetti never gets a frame to render. Stash the intent in
// sessionStorage so the destination page can fire it on boot. Short TTL
// because the marker is "this navigation just happened"; older entries
// belong to a different action.
const PENDING_KEY = '__steppr_pending_completion';
const PENDING_TTL_MS = 10_000;

export function schedulePendingCompletion(
  config: FlowConfig | undefined,
  primaryColor: string | undefined,
): void {
  const kind: OnCompleteAnimation =
    (config?.onCompleteAnimation as OnCompleteAnimation | undefined) ?? 'none';
  if (kind === 'none') return;
  try {
    sessionStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ kind, primaryColor: primaryColor ?? null, at: Date.now() }),
    );
  } catch {
    // Some hosts disable sessionStorage (cookieless contexts, sandboxed
    // iframes). The animation is best-effort polish; failing silently is
    // the right behaviour.
  }
}

export function flushPendingCompletion(primaryColor: string | undefined): void {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(PENDING_KEY);
  } catch {
    return;
  }
  if (!raw) return;
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
  try {
    const data = JSON.parse(raw) as {
      kind: OnCompleteAnimation;
      primaryColor: string | null;
      at: number;
    };
    if (Date.now() - data.at > PENDING_TTL_MS) return;
    // Prefer the colour stashed at completion time (it's what the user
    // saw across the rest of the tour), but fall back to the destination
    // page's theme if for some reason it wasn't recorded.
    const color = data.primaryColor ?? primaryColor;
    if (data.kind === 'confetti') fireConfetti(color ?? undefined);
  } catch {
    /* malformed marker - drop it */
  }
}

function fireConfetti(primaryColor: string | undefined) {
  const palette = paletteFrom(primaryColor);
  // Skip when the host doc is hidden - saves CPU and avoids a particle
  // burst the user wouldn't see anyway. Most browsers throttle requestAnimationFrame
  // for hidden tabs, but canvas-confetti will queue a backlog otherwise.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return;
  }
  // Note: deliberately not passing `disableForReducedMotion`. The author
  // explicitly opted into a one-shot celebration; the burst is ~1.5s and
  // fires once at completion, which is meaningfully different from the
  // ambient / repeating animations the reduced-motion preference is
  // primarily targeting. Customers who want stricter accessibility can
  // leave the per-tour animation set to "None".
  const base: confetti.Options = {
    particleCount: 60,
    spread: 70,
    startVelocity: 35,
    ticks: 200,
    colors: palette,
  };
  void confetti({ ...base, origin: { x: 0.2, y: 0.85 }, angle: 60 });
  void confetti({ ...base, origin: { x: 0.8, y: 0.85 }, angle: 120 });
}

function paletteFrom(primaryColor: string | undefined): string[] {
  if (!primaryColor) {
    // Default brand-agnostic palette when no theme primary is set yet.
    return ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899'];
  }
  return [
    primaryColor,
    lighten(primaryColor, 0.35),
    lighten(primaryColor, 0.65),
    '#ffffff',
  ];
}

// Lighten a 6-digit hex color by `amount` (0..1). Returns the input
// untouched if it isn't a recognised 6-digit hex - confetti tolerates any
// CSS color string, so falling back is harmless.
function lighten(hex: string, amount: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const num = parseInt(m[1]!, 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const lift = (c: number) => Math.min(255, Math.round(c + (255 - c) * amount));
  const next = (lift(r) << 16) | (lift(g) << 8) | lift(b);
  return '#' + next.toString(16).padStart(6, '0');
}
